import React, { useEffect, useState, useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { KeySignature } from '@chordbaker/music-theory';
import { ModifierType, modifierCodeToModifierType, buildNote } from '../utils/noteHelpers';
import ProhibitedIcon from './icons/ProhibitedIcon';
import CloseIcon from './icons/CloseIcon';

const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: theme.colors.modalOverlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
    zIndex: 50,
  },
  modalContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.colors.modalBackground,
    borderRadius: theme.radii.lg,
    padding: theme.spacing.xl,
    width: '100%',
    boxShadow: theme.shadows.modal,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: theme.typography.sizes.xxl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    margin: 0,
  },
  closeButton: {
    padding: theme.spacing.xs,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
  scrollContentContainer: {
    paddingBottom: theme.spacing.lg,
  },
  notesContainer: {
    display: 'flex',
    gap: theme.spacing.sm,
    justifyContent: 'center',
  },
  optionButton: {
    backgroundColor: theme.colors.surfaceSecondary,
    border: 'none',
    borderRadius: theme.radii.md,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    height: theme.sizes.button.height - 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  optionButtonSelected: {
    backgroundColor: theme.colors.key,
  },
  optionButtonSelectedModifier: {
    backgroundColor: theme.colors.keySecondary,
  },
  optionButtonSelectedKeyType: {
    backgroundColor: theme.colors.keyTertiary,
  },
  optionButtonDisabled: {
    opacity: theme.opacity.disabled,
    cursor: 'not-allowed',
  },
  optionButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.text,
    fontWeight: theme.typography.weights.normal,
    textAlign: 'center' as const,
    margin: 0,
  },
  optionButtonTextSelected: {
    color: theme.colors.textInverse,
  },
  optionButtonTextDisabled: {
    color: theme.colors.textTertiary,
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.border,
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
    width: '100%',
  },
  actionContainer: {
    display: 'flex',
    gap: theme.spacing.md,
    justifyContent: 'center',
  },
  actionButton: {
    backgroundColor: theme.colors.surfaceSecondary,
    border: 'none',
    borderRadius: theme.radii.md,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  actionButtonPrimary: {
    backgroundColor: theme.colors.primary,
  },
  actionButtonText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.text,
    fontWeight: theme.typography.weights.medium,
    margin: 0,
  },
  actionButtonTextPrimary: {
    color: theme.colors.textInverse,
  },
  footerText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'left' as const,
    lineHeight: theme.typography.lineHeights.normal,
    marginTop: theme.spacing.sm,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  },
});

interface KeySelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectKey: (key: KeySignature | null) => void;
  currentKeyDisplay: KeySignature | null;
}

type KeyType = 'major' | 'minor';

const KeySelectorModal: React.FC<KeySelectorModalProps> = ({
  isOpen,
  onClose,
  onSelectKey,
  currentKeyDisplay,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const [selectedNote, setSelectedNote] = useState<string | null>(null);
  const [selectedModifier, setSelectedModifier] = useState<ModifierType>('natural');
  const [keyType, setKeyType] = useState<KeyType>('major');
  const [isNoKey, setIsNoKey] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      if (!currentKeyDisplay) {
        // No key selected - set to "No Key" state
        setIsNoKey(true);
        setSelectedNote(null);
        setSelectedModifier('natural');
        setKeyType('major');
      } else {
        // Extract from KeySignature object
        setIsNoKey(false);
        
        // Extract note name and modifier from KeySignature
        setSelectedNote(currentKeyDisplay.note.noteName);
        setSelectedModifier(modifierCodeToModifierType(currentKeyDisplay.note.modifier));
        
        // Extract mode (maj/min)
        setKeyType(currentKeyDisplay.mode === 'min' ? 'minor' : 'major');
      }
    }
  }, [isOpen, currentKeyDisplay]);

  if (!isOpen) {
    return null;
  }

  const handleNoteSelect = (note: string) => {
    setSelectedNote(note);
    setIsNoKey(false);
  };

  const handleNoKeySelect = () => {
    setIsNoKey(true);
    setSelectedNote(null);
    setSelectedModifier('natural');
    setKeyType('major');
  };

  const handleModifierSelect = (modifier: ModifierType) => {
    setSelectedModifier(modifier);
    if (selectedNote) setIsNoKey(false);
  };

  const handleKeyTypeSelect = (type: KeyType) => {
    setKeyType(type);
    if (selectedNote) setIsNoKey(false);
  };

  const handleApply = () => {
    if (isNoKey) {
      // Return null for "No Key" selection
      onSelectKey(null);
    } else if (selectedNote) {
      // Build KeySignature object from selected values
      const keySignature: KeySignature = {
        note: buildNote(selectedNote as any, selectedModifier),
        mode: keyType === 'minor' ? 'min' : 'maj',
      };
      
      onSelectKey(keySignature);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const naturalNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const modifiers: Array<{ type: ModifierType; display: string }> = [
    { type: 'natural', display: '-' },
    { type: 'flat', display: '♭' },
    { type: 'sharp', display: '♯' },
  ];
  const keyTypes: Array<{ type: KeyType; display: string }> = [
    { type: 'major', display: 'Major' },
    { type: 'minor', display: 'Minor' },
  ];

  // Calculate button widths
  const modalContentWidth = Math.min(window.innerWidth - (theme.spacing.lg * 2), 400);
  const containerWidth = modalContentWidth - (theme.spacing.xl * 2); // padding on each side
  const buttonWidth = (containerWidth - (4 - 1) * (theme.spacing.xs + 2)) / 4; // 4 buttons per row with gaps
  const modifierButtonWidth = (containerWidth - (3 - 1) * (theme.spacing.xs + 2)) / 3; // 3 buttons per row with gaps
  const keyTypeButtonWidth = (containerWidth - (2 - 1) * (theme.spacing.xs + 2)) / 2; // 2 buttons per row with gaps

  // Define the 4 rows as per RN specification
  const rows = [
    [
      { type: 'noKey', key: 'noKey' },
      { type: 'note', key: naturalNotes[0], note: naturalNotes[0] },
      { type: 'note', key: naturalNotes[1], note: naturalNotes[1] },
      { type: 'note', key: naturalNotes[2], note: naturalNotes[2] },
    ],
    [
      { type: 'note', key: naturalNotes[3], note: naturalNotes[3] },
      { type: 'note', key: naturalNotes[4], note: naturalNotes[4] },
      { type: 'note', key: naturalNotes[5], note: naturalNotes[5] },
      { type: 'note', key: naturalNotes[6], note: naturalNotes[6] },
    ],
    [
      { type: 'modifier', key: modifiers[0].type, modifier: modifiers[0] },
      { type: 'modifier', key: modifiers[1].type, modifier: modifiers[1] },
      { type: 'modifier', key: modifiers[2].type, modifier: modifiers[2] },
    ],
    [
      { type: 'keyType', key: keyTypes[0].type, keyType: keyTypes[0] },
      { type: 'keyType', key: keyTypes[1].type, keyType: keyTypes[1] },
    ],
  ];

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
    >
      <div
        style={{ ...styles.modalContainer, width: modalContentWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalContent}>
          {/* Header */}
          <div style={styles.header}>
            <h2 style={styles.title}>Select a Key</h2>
            <button
              onClick={handleCancel}
              style={styles.closeButton}
              aria-label="Close key selector"
            >
              <CloseIcon size={20} color={theme.colors.textSecondary} />
            </button>
          </div>

          <div style={styles.scrollContentContainer}>
            {/* Fixed 4-Row Layout */}
            {rows.map((row, rowIdx) => (
              <div 
                key={rowIdx} 
                style={{
                  ...styles.notesContainer,
                  marginBottom: rowIdx === rows.length - 1 ? 0 : theme.spacing.sm
                }}
              >
                {row.map((btn) => {
                  if (btn.type === 'noKey') {
                    return (
                      <button
                        key={btn.key}
                        style={{
                          ...styles.optionButton,
                          width: buttonWidth,
                          height: theme.sizes.button.height - 12,
                          ...(isNoKey ? styles.optionButtonSelected : {}),
                        }}
                        onClick={handleNoKeySelect}
                      >
                        <ProhibitedIcon
                          size={theme.sizes.icon.sm}
                          color={isNoKey ? theme.colors.textInverse : theme.colors.text}
                        />
                      </button>
                    );
                  } else if (btn.type === 'note') {
                    const noteBtn = btn as { type: 'note'; key: string; note: string };
                    return (
                      <button
                        key={noteBtn.key}
                        style={{
                          ...styles.optionButton,
                          width: buttonWidth,
                          height: theme.sizes.button.height - 12,
                          ...(selectedNote === noteBtn.note && !isNoKey ? styles.optionButtonSelected : {}),
                        }}
                        onClick={() => handleNoteSelect(noteBtn.note)}
                      >
                        <span style={{
                          ...styles.optionButtonText,
                          ...(selectedNote === noteBtn.note && !isNoKey ? styles.optionButtonTextSelected : {}),
                        }}>
                          {noteBtn.note}
                        </span>
                      </button>
                    );
                  } else if (btn.type === 'modifier') {
                    const modBtn = btn as { type: 'modifier'; key: string; modifier: { type: ModifierType; display: string } };
                    return (
                      <button
                        key={modBtn.key}
                        style={{
                          ...styles.optionButton,
                          width: modifierButtonWidth,
                          height: theme.sizes.button.height - 12,
                          ...(selectedModifier === modBtn.modifier.type && !isNoKey ? styles.optionButtonSelectedModifier : {}),
                          ...(isNoKey ? styles.optionButtonDisabled : {}),
                        }}
                        onClick={() => handleModifierSelect(modBtn.modifier.type)}
                        disabled={isNoKey}
                      >
                        <span style={{
                          ...styles.optionButtonText,
                          ...(selectedModifier === modBtn.modifier.type && !isNoKey ? styles.optionButtonTextSelected : {}),
                          ...(isNoKey ? styles.optionButtonTextDisabled : {}),
                        }}>
                          {modBtn.modifier.display}
                        </span>
                      </button>
                    );
                  } else if (btn.type === 'keyType') {
                    const keyTypeBtn = btn as { type: 'keyType'; key: string; keyType: { type: KeyType; display: string } };
                    return (
                      <button
                        key={keyTypeBtn.key}
                        style={{
                          ...styles.optionButton,
                          width: keyTypeButtonWidth,
                          height: theme.sizes.button.height - 12,
                          ...(keyType === keyTypeBtn.keyType.type && !isNoKey ? styles.optionButtonSelectedKeyType : {}),
                          ...(isNoKey ? styles.optionButtonDisabled : {}),
                        }}
                        onClick={() => handleKeyTypeSelect(keyTypeBtn.keyType.type)}
                        disabled={isNoKey}
                      >
                        <span style={{
                          ...styles.optionButtonText,
                          ...(keyType === keyTypeBtn.keyType.type && !isNoKey ? styles.optionButtonTextSelected : {}),
                          ...(isNoKey ? styles.optionButtonTextDisabled : {}),
                        }}>
                          {keyTypeBtn.keyType.display}
                        </span>
                      </button>
                    );
                  }
                  return null;
                })}
              </div>
            ))}

            {/* Visual Separator */}
            <div style={styles.separator} />

            {/* Action Buttons */}
            <div style={styles.actionContainer}>
              <button
                style={styles.actionButton}
                onClick={handleCancel}
              >
                <span style={styles.actionButtonText}>Cancel</span>
              </button>

              <button
                style={{
                  ...styles.actionButton,
                  ...styles.actionButtonPrimary,
                }}
                onClick={handleApply}
              >
                <span style={{
                  ...styles.actionButtonText,
                  ...styles.actionButtonTextPrimary,
                }}>Apply</span>
              </button>
            </div>
          </div>

          {/* Footer text */}
          <p style={styles.footerText}>
            Selecting a key will enable the display of chord degrees relative to this key. The system isn't designed to handle advanced cases like modulations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeySelectorModal;